import { normalizeHex } from "./harmonicPalette";
import { ROWS_PER_ROPE_INCH } from "./knotCalc";
import { isEmptyBead } from "./stringingOrder";

/** Longest span converted from inches, so a typo cannot build a huge chart. */
export const MAX_DISSOLVE_ROWS = 4000;

export const DISSOLVE_TYPES = [
  {
    id: "desaturate",
    label: "Desaturate",
    detail:
      "Each variation adds a paler shade of every color. The last variation is the palest.",
  },
  {
    id: "corruption",
    label: "Corruption",
    detail: "Beads shift onto other colors already on the chart.",
  },
  {
    id: "fidelity",
    label: "Fidelity",
    detail: "The motif is redrawn in coarser blocks until the detail is gone.",
  },
  {
    id: "replacement",
    label: "Pixel replacement",
    detail: "Beads flip one by one to the most common color already on the chart.",
  },
  {
    id: "dither",
    label: "Dither",
    detail:
      "An ordered dither fades the motif into a color from the chart. The end of the span is that color.",
  },
];

const BAYER_4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];

/**
 * Rows for a length of strung beads. Beads around is how many rows each
 * strung inch adds. Returns 0 when either value is not positive.
 */
export function rowsForSpan(lengthInches, beadsAround) {
  const inches = Number(lengthInches);
  const around = Math.floor(Number(beadsAround));
  if (!(inches > 0) || !(around > 0)) return 0;
  const rows = Math.round(inches * around);
  if (rows < 1) return 1;
  return Math.min(MAX_DISSOLVE_ROWS, rows);
}

/**
 * Rows for a finished rope length. One inch of rope is 16 rows, at any
 * beads-around. Strung inches are a separate measurement.
 */
export function rowsForRopeSpan(ropeInches) {
  const inches = Number(ropeInches);
  if (!(inches > 0)) return 0;
  const rows = Math.round(inches * ROWS_PER_ROPE_INCH);
  if (rows < 1) return 0;
  return Math.min(MAX_DISSOLVE_ROWS, rows);
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function padPattern(pattern, total, defaultColor) {
  return Array.from({ length: total }, (_, i) => {
    const value = Array.isArray(pattern) ? pattern[i] : undefined;
    return value == null || value === "" ? defaultColor : value;
  });
}

/** Chart rows from the stringing start (visual top) to the end (visual bottom). */
function chartRowsTopToBottom(pattern, cols, rows, defaultColor) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const arr = padPattern(pattern, c * r, defaultColor);
  const out = [];
  for (let fromTop = 0; fromTop < r; fromTop++) {
    const start = (r - fromTop - 1) * c;
    out.push(arr.slice(start, start + c));
  }
  return out;
}

function motifRows(pattern, cols, rows, defaultColor) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const all = chartRowsTopToBottom(pattern, c, rows, defaultColor);
  let start = 0;
  let end = all.length;
  const blank = (row) => row.every((color) => isEmptyBead(color, defaultColor));
  while (start < end && blank(all[start])) start += 1;
  while (end > start && blank(all[end - 1])) end -= 1;
  if (start === end) return [Array.from({ length: c }, () => defaultColor)];
  return all.slice(start, end).map((row) => row.slice());
}

function tileMotif(motif, count, startIndex = 0) {
  const out = [];
  const n = motif.length;
  for (let i = 0; i < count; i++) {
    out.push(motif[(startIndex + i) % n].slice());
  }
  return out;
}

function progress(index, count) {
  if (count <= 1) return 1;
  return index / (count - 1);
}

function rowsToPattern(rowsTopToBottom, defaultColor) {
  const r = rowsTopToBottom.length;
  const c = rowsTopToBottom[0]?.length || 1;
  const pattern = Array.from({ length: c * r }, () => defaultColor);
  for (let fromTop = 0; fromTop < r; fromTop++) {
    const start = (r - fromTop - 1) * c;
    const row = rowsTopToBottom[fromTop];
    for (let col = 0; col < c; col++) pattern[start + col] = row[col];
  }
  return pattern;
}

function hexToHsl(hex) {
  const n = normalizeHex(hex);
  if (!n) return null;
  const r = parseInt(n.slice(1, 3), 16) / 255;
  const g = parseInt(n.slice(3, 5), 16) / 255;
  const b = parseInt(n.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  const hh = ((h % 360) + 360) % 360;
  const ss = clamp(s, 0, 100) / 100;
  const ll = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = ll - c / 2;
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hh < 60) {
    r1 = c;
    g1 = x;
  } else if (hh < 120) {
    r1 = x;
    g1 = c;
  } else if (hh < 180) {
    g1 = c;
    b1 = x;
  } else if (hh < 240) {
    g1 = x;
    b1 = c;
  } else if (hh < 300) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }
  const toByte = (v) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toByte(r1)}${toByte(g1)}${toByte(b1)}`;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

/** How many paler steps desaturate will take. Each step shades every chart color. */
export const MAX_DESATURATE_VARIATIONS = 5;

const DEFAULT_DESATURATE_VARIATIONS = 3;

function colorKey(color) {
  return normalizeHex(color) || String(color || "");
}

/** Most common bead in the motif. Ties keep the first color encountered. */
function fieldColor(motif, fallback) {
  const cells = motif.flat();
  if (cells.length === 0) return fallback;
  return majorityColor(cells);
}

/**
 * Colors a dither can fade into, in chart order, plus the most common one.
 * The most common color is the default when none is chosen.
 */
export function ditherColorChoices(pattern, cols, rows, defaultColor) {
  const motif = motifRows(pattern, cols, rows, defaultColor);
  return {
    colors: motifPalette(motif, defaultColor),
    dominant: fieldColor(motif, defaultColor),
  };
}

/** Chart color matching `groundColor`, or the most common color when it does not. */
function resolveDitherColor(motif, fallback, groundColor) {
  const wanted = colorKey(groundColor);
  if (wanted) {
    const match = motifPalette(motif, fallback).find(
      (color) => colorKey(color) === wanted
    );
    if (match) return match;
  }
  return fieldColor(motif, fallback);
}

function rgbParts(hex) {
  const n = normalizeHex(hex);
  if (!n) return null;
  return [
    parseInt(n.slice(1, 3), 16),
    parseInt(n.slice(3, 5), 16),
    parseInt(n.slice(5, 7), 16),
  ];
}

function colorDistance(a, b) {
  const left = rgbParts(a);
  const right = rgbParts(b);
  if (!left || !right) return Number.POSITIVE_INFINITY;
  const dr = left[0] - right[0];
  const dg = left[1] - right[1];
  const db = left[2] - right[2];
  return dr * dr + dg * dg + db * db;
}

function nearestColor(ideal, options) {
  let best = options[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const option of options) {
    const distance = colorDistance(ideal, option);
    if (distance < bestDistance) {
      best = option;
      bestDistance = distance;
    }
  }
  return best;
}

function sourceColors(motif) {
  const map = new Map();
  for (const row of motif) {
    for (const color of row) {
      const key = colorKey(color);
      if (!key) continue;
      const entry = map.get(key);
      if (entry) entry.count += 1;
      else map.set(key, { color, count: 1, hsl: hexToHsl(color) });
    }
  }
  return [...map.values()];
}

function isChromatic(color) {
  const parts = rgbParts(color);
  if (!parts) return false;
  return Math.max(...parts) - Math.min(...parts) >= 18;
}

function variationCount(variations) {
  const parsed = Math.round(Number(variations));
  const value = Number.isFinite(parsed) ? parsed : DEFAULT_DESATURATE_VARIATIONS;
  return clamp(value, 1, MAX_DESATURATE_VARIATIONS);
}

/** Pale end state: same hue, little saturation, high lightness. */
const PALE_SATURATION = 0.15;
const PALE_LIGHTNESS = 90;

function mixPale(color, t) {
  if (t <= 0) return color;
  const from = hexToHsl(color);
  if (!from) return color;
  return hslToHex(
    from[0],
    lerp(from[1], from[1] * PALE_SATURATION, t),
    lerp(from[2], Math.max(from[2], PALE_LIGHTNESS), t)
  );
}

/**
 * One paler shade of every chart color per variation. The span snaps to
 * this list, so a long piece cannot mint a new color on every row.
 */
function buildDesaturateShades(sources, variations) {
  const steps = variationCount(variations);
  const chromatic = sources.filter((source) => isChromatic(source.color));
  const colors = chromatic.length > 0 ? chromatic : sources;
  const shades = [];
  const seen = new Set(sources.map((source) => colorKey(source.color)));
  const push = (hex) => {
    const key = colorKey(hex);
    if (!key || seen.has(key)) return;
    seen.add(key);
    shades.push(hex);
  };
  for (let step = 1; step <= steps; step++) {
    const t = step / steps;
    for (const source of colors) {
      push(mixPale(source.color, t));
    }
  }
  return shades;
}

/**
 * Each color washes toward a paler version of itself, one shade per variation.
 */
function desaturateRows(base, motif, variations) {
  const sources = sourceColors(motif);
  if (sources.length === 0) return base.map((row) => row.slice());
  const shades = buildDesaturateShades(sources, variations);
  if (shades.length === 0) return base.map((row) => row.slice());

  return base.map((row, index) => {
    const t = progress(index, base.length);
    if (t <= 0) return row.slice();
    return row.map((color) => nearestColor(mixPale(color, t), [color, ...shades]));
  });
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function motifPalette(motif, ground) {
  const seen = new Set();
  const colors = [];
  for (const row of motif) {
    for (const color of row) {
      const key = normalizeHex(color) || String(color);
      if (seen.has(key)) continue;
      seen.add(key);
      colors.push(color);
    }
  }
  if (colors.length === 0) colors.push(ground);
  return colors;
}

function corruptRows(base, ground, palette, strength, seed) {
  const rng = mulberry32(seed);
  const amount = clamp(Number(strength) || 0, 0, 1);
  return base.map((row, ri) => {
    const p = progress(ri, base.length) * amount;
    return row.map((color, ci) => {
      if (p <= 0 || rng() > p) return color;
      const roll = rng();
      if (roll < 0.4) return ground;
      if (roll < 0.7) {
        const dir = rng() < 0.5 ? -1 : 1;
        return row[(ci + dir + row.length) % row.length];
      }
      return palette[Math.floor(rng() * palette.length)];
    });
  });
}

function majorityColor(cells) {
  const counts = new Map();
  for (const color of cells) {
    const key = normalizeHex(color) || String(color || "");
    const entry = counts.get(key);
    if (entry) entry.count += 1;
    else counts.set(key, { count: 1, color });
  }
  let best = cells[0];
  let bestCount = -1;
  for (const entry of counts.values()) {
    if (entry.count > bestCount) {
      best = entry.color;
      bestCount = entry.count;
    }
  }
  return best;
}

function fidelityRows(base, maxBlock) {
  const cols = base[0].length;
  const limit = clamp(Math.round(Number(maxBlock) || 1), 1, cols);
  const out = base.map((row) => row.slice());
  let r = 0;
  while (r < base.length) {
    const t = progress(r, base.length);
    const block = clamp(Math.round(lerp(1, limit, t)), 1, cols);
    const rowSpan = Math.min(block, base.length - r);
    for (let c = 0; c < cols; c += block) {
      const colSpan = Math.min(block, cols - c);
      const cells = [];
      for (let rr = 0; rr < rowSpan; rr++) {
        for (let cc = 0; cc < colSpan; cc++) {
          cells.push(base[r + rr][c + cc]);
        }
      }
      const color = majorityColor(cells);
      for (let rr = 0; rr < rowSpan; rr++) {
        for (let cc = 0; cc < colSpan; cc++) {
          out[r + rr][c + cc] = color;
        }
      }
    }
    r += rowSpan;
  }
  return out;
}

function unitThreshold(row, col, seed) {
  const rng = mulberry32(
    (Math.imul(seed >>> 0, 1000003) +
      Math.imul(row + 1, 1009) +
      Math.imul(col + 1, 9176)) >>>
      0
  );
  return (Math.floor(rng() * 997) + 0.5) / 997;
}

function replacementRows(base, ground, seed) {
  return base.map((row, ri) => {
    const t = progress(ri, base.length);
    return row.map((color, ci) =>
      unitThreshold(ri, ci, seed) < t ? ground : color
    );
  });
}

function bayerThreshold(row, col) {
  return (BAYER_4[row & 3][col & 3] + 0.5) / 16;
}

function ditherRows(base, ground) {
  return base.map((row, ri) => {
    const t = progress(ri, base.length);
    return row.map((color, ci) =>
      t >= bayerThreshold(ri, ci) ? ground : color
    );
  });
}

/**
 * Keep the current chart, then continue it for `dissolveRows` while the
 * selected dissolve ramps from clean at the seam to fully dissolved at the
 * end of the piece. Stringing still starts at the top of the chart.
 */
export function dissolvePattern({
  pattern,
  cols,
  rows,
  defaultColor,
  dissolveRows,
  type,
  seed = 1,
  maxBlock,
  strength = 1,
  variations,
  groundColor,
}) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const sourceRows = Math.max(0, Math.floor(Number(rows)) || 0);
  const fadeCount = clamp(Math.floor(Number(dissolveRows)) || 0, 0, MAX_DISSOLVE_ROWS);
  const chart = chartRowsTopToBottom(pattern, c, sourceRows, defaultColor);
  const motif = motifRows(pattern, c, sourceRows, defaultColor);
  const phase = chart.length % motif.length;
  const field = fieldColor(motif, defaultColor);
  let faded = [];
  if (fadeCount > 0) {
    const base = tileMotif(motif, fadeCount, phase);
    if (type === "corruption") {
      faded = corruptRows(
        base,
        field,
        motifPalette(motif, field),
        strength,
        Math.floor(Number(seed)) || 1
      );
    } else if (type === "fidelity") {
      const block = maxBlock == null || maxBlock === "" ? Math.min(c, 4) : maxBlock;
      faded = fidelityRows(base, block);
    } else if (type === "replacement") {
      faded = replacementRows(base, field, Math.floor(Number(seed)) || 1);
    } else if (type === "dither") {
      faded = ditherRows(base, resolveDitherColor(motif, defaultColor, groundColor));
    } else {
      faded = desaturateRows(base, motif, variations);
    }
  }

  const combined = chart.concat(faded);
  if (combined.length === 0) {
    return {
      pattern: padPattern(pattern, c * sourceRows, defaultColor),
      rows: sourceRows,
      cols: c,
    };
  }

  return {
    pattern: rowsToPattern(combined, defaultColor),
    rows: combined.length,
    cols: c,
  };
}
